import React, { useState } from 'react';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';
import Button from './ui/Button';

interface Artist {
  id: string;
  name: string;
  email: string;
  telegram?: string;
}

interface Project {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  description?: string;
  producerId?: string;
  managerId?: string;
  producerName?: string;
  managerName?: string;
}

interface ProjectInvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  artists: Artist[];
  projects: Project[];
  onSendInvitation: (projectId: string, artistIds: string[], method: 'email' | 'telegram', hideProjectName: boolean) => void;
}

const ProjectInvitationModal: React.FC<ProjectInvitationModalProps> = ({
  isOpen,
  onClose,
  artists,
  projects,
  onSendInvitation
}) => {
  const { t } = useTranslation();
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [invitationMethod, setInvitationMethod] = useState<'email' | 'telegram'>('email');
  const [hideProjectName, setHideProjectName] = useState(false);

  if (!isOpen) return null;

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  // Check if artists have required contact method
  const artistsWithEmail = artists.filter(a => a.email);
  const artistsWithTelegram = artists.filter(a => a.telegram);

  const canSendEmail = artistsWithEmail.length > 0;
  const canSendTelegram = artistsWithTelegram.length > 0;

  const handleSend = () => {
    if (!selectedProjectId) {
      alert(t('projectInvitation.selectProject'));
      return;
    }

    const artistIds = artists.map(a => a.id);
    onSendInvitation(selectedProjectId, artistIds, invitationMethod, hideProjectName);
    onClose();
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <ICONS.Mail className="w-6 h-6 text-indigo-400" />
            <h2 className="text-2xl font-bold text-white">
              {t('projectInvitation.title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <ICONS.X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Selected Artists */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('projectInvitation.selectedArtists')} ({artists.length})
            </label>
            <div className="bg-gray-800 rounded-lg p-4 max-h-32 overflow-y-auto">
              <div className="space-y-2">
                {artists.map(artist => (
                  <div key={artist.id} className="flex items-center justify-between text-sm">
                    <span className="text-white">{artist.name}</span>
                    <div className="flex gap-2 text-xs text-gray-400">
                      {artist.email && (
                        <span className="flex items-center gap-1">
                          <ICONS.Mail className="w-3 h-3" />
                          {artist.email}
                        </span>
                      )}
                      {artist.telegram && (
                        <span className="flex items-center gap-1">
                          <ICONS.MessageSquare className="w-3 h-3" />
                          {artist.telegram}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('projectInvitation.selectProject')} *
            </label>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">{t('projectInvitation.chooseProject')}</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* Project Details Preview */}
          {selectedProject && (
            <div className="bg-gray-800 rounded-lg p-4 space-y-2">
              <h3 className="text-sm font-medium text-gray-300 mb-2">
                {t('projectInvitation.projectDetails')}
              </h3>
              <div className="text-sm space-y-1 text-gray-400">
                {!hideProjectName && (
                  <p>
                    <span className="font-medium text-gray-300">{t('projectInvitation.projectName')}:</span> {selectedProject.name}
                  </p>
                )}
                {hideProjectName && (
                  <p className="text-yellow-400 italic">
                    {t('projectInvitation.projectNameHidden')}
                  </p>
                )}
                <p>
                  <span className="font-medium text-gray-300">{t('projectInvitation.dates')}:</span>{' '}
                  {formatDate(selectedProject.startDate)} — {formatDate(selectedProject.endDate)}
                </p>
                {selectedProject.producerName && (
                  <p>
                    <span className="font-medium text-gray-300">{t('projectInvitation.producer')}:</span> {selectedProject.producerName}
                  </p>
                )}
                {selectedProject.managerName && (
                  <p>
                    <span className="font-medium text-gray-300">{t('projectInvitation.supervisor')}:</span> {selectedProject.managerName}
                  </p>
                )}
                {selectedProject.description && (
                  <p>
                    <span className="font-medium text-gray-300">{t('projectInvitation.description')}:</span> {selectedProject.description}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Hide Project Name Option */}
          <div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hideProjectName}
                onChange={(e) => setHideProjectName(e.target.checked)}
                className="rounded text-indigo-500 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-300">
                {t('projectInvitation.hideProjectName')} (NDA)
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              {t('projectInvitation.hideProjectNameHint')}
            </p>
          </div>

          {/* Invitation Method */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('projectInvitation.invitationMethod')} *
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setInvitationMethod('email')}
                disabled={!canSendEmail}
                className={`p-4 rounded-lg border-2 transition-all ${
                  invitationMethod === 'email'
                    ? 'border-indigo-500 bg-indigo-500/10'
                    : 'border-gray-700 bg-gray-800'
                } ${!canSendEmail ? 'opacity-50 cursor-not-allowed' : 'hover:border-indigo-500/50'}`}
              >
                <ICONS.Mail className="w-6 h-6 mx-auto mb-2 text-indigo-400" />
                <div className="text-white font-medium">Email</div>
                <div className="text-xs text-gray-400 mt-1">
                  {artistsWithEmail.length} / {artists.length} {t('projectInvitation.available')}
                </div>
              </button>

              <button
                onClick={() => setInvitationMethod('telegram')}
                disabled={!canSendTelegram}
                className={`p-4 rounded-lg border-2 transition-all ${
                  invitationMethod === 'telegram'
                    ? 'border-indigo-500 bg-indigo-500/10'
                    : 'border-gray-700 bg-gray-800'
                } ${!canSendTelegram ? 'opacity-50 cursor-not-allowed' : 'hover:border-indigo-500/50'}`}
              >
                <ICONS.MessageSquare className="w-6 h-6 mx-auto mb-2 text-indigo-400" />
                <div className="text-white font-medium">Telegram</div>
                <div className="text-xs text-gray-400 mt-1">
                  {artistsWithTelegram.length} / {artists.length} {t('projectInvitation.available')}
                </div>
              </button>
            </div>
          </div>

          {/* Warning if some artists don't have contact */}
          {invitationMethod === 'email' && artistsWithEmail.length < artists.length && (
            <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-3 text-sm text-yellow-400">
              <p>
                {t('projectInvitation.someArtistsNoEmail', { 
                  count: artists.length - artistsWithEmail.length 
                })}
              </p>
            </div>
          )}

          {invitationMethod === 'telegram' && artistsWithTelegram.length < artists.length && (
            <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-3 text-sm text-yellow-400">
              <p>
                {t('projectInvitation.someArtistsNoTelegram', { 
                  count: artists.length - artistsWithTelegram.length 
                })}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-700">
          <Button
            onClick={onClose}
            variant="secondary"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSend}
            disabled={!selectedProjectId || (invitationMethod === 'email' && !canSendEmail) || (invitationMethod === 'telegram' && !canSendTelegram)}
          >
            <ICONS.Send className="w-4 h-4 mr-2" />
            {t('projectInvitation.sendInvitation')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectInvitationModal;

